import { isEmpty, isArray } from 'lodash';
import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios, { AxiosError } from 'axios';
import axiosRetry from 'axios-retry';
import DeviceInfo from 'react-native-device-info';

const debug = require('debug')('se:api:adserver');
import {
  CONDO_CORP_ID,
  PROPERTY_MANAGER_ID,
  ZIP_POSTAL,
} from '../../lib/constants';
import axiosInstance, { axiosInstancePlain } from '../AxiosConfig';

function retryCondition(e: any) {
  if (e.response) {
    console.debug('retryCondition', JSON.stringify(e));
  } else {
    console.debug('no e.response.  not retring');
    return false;
  }
  return (
    !!e.response && (e.response.status === 500 || e.response.status === 429)
  );
}

axiosRetry(axiosInstance, {
  retries: 0,
  retryCondition,
  retryDelay: axiosRetry.exponentialDelay,
});

// For the PMS Backend (not explore)
export interface PropertyManager {
  id: number;
  body: string;
  call_center_button_title?: string;
  call_center_enable?: boolean;
  call_center_heading?: string;
  call_center_phone_number?: string;
  condo_corporations: CondoCorporation[];
  contact_body: string;
  contact_image: ImageFormat;
  hero: ImageFormat;
  landing_screen_image?: ImageFormat;
  locale: Locale;
  localizations?: any[];
  name: string;
  created_at: string;
  updated_at: string;
}

// For the PMS Backend (not explore)
export interface CondoCorporation {
  id: number;
  name: string;
  website: string;
  phone: string;
  email: string;
  // admin_user: null;
  created_at: string;
  updated_at: string;
  property_manager: number;
  building_code: string;
  explore_corp_id?: number;
  yardi_property_id: string;
  yardi_property_name: string;
  yardi_reference_id: string;
  explore_condo_id: string;
  yardi_id: string;
  useInternalAuth: boolean;
  locale: Locale;
  home_property_id?: string;
  zip_postal?: string;
  call_center_heading?: string;
  call_center_phone_number?: string;
  call_center_button_title?: string;
  call_center_settings?: string;
  property_manager_name?: string;
  property_manager_body?: string;
  property_manager_contact_body?: string;
  // use_default_pmc_values?: boolean,
  override_default_pmc_values?: boolean;
  property_manager_hero_image?: ImageFormat;
  property_manager_contact_image?: ImageFormat;
}

export interface Category {
  id: string;
  title: string;
  image: Image;
  category_type: Provider;
}

export interface ImageFormat {
  id: number;
  name: string;
  alternativeText?: string;
  caption?: string;
  width: number;
  height: number;
  formats?: {
    thumbnail: {
      name: string;
      hash: string;
      ext: string;
      mime: string;
      width: number;
      height: number;
      size: number;
      path?: string;
      url: string;
    };
  };
  hash: string;
  ext: string;
  mime: string;
  size: number;
  url: string;
  previewUrl?: string;
  provider: string;
  provider_metadata?: any; // probably an object
  created_at: string;
  updated_at: string;
}

export interface Image {
  id: number;
  name: string;
  alternativeText: string;
  caption: string;
  width: number;
  height: number;
  formats: {
    thumbnail: ImageFormat;
    large: ImageFormat;
    medium: ImageFormat;
    small: ImageFormat;
  };
  hash: string;
  ext: string;
  mime: string;
  size: number;
  url: string;
  previewUrl?: string;
  provider: string;
  provider_metadata?: object;
  created_at: string;
  updated_at: string;
}

export interface Gallery {
  id: number;
  title: string;
  created_at: string;
  updated_at: string;
  advertiser: number;
  images: Image[];
}

export interface Campaign {
  id: number;
  name: string;
  active: boolean;
  start_datetime: string;
  stop_datetime: string;
  advertiser: number;
  legacy_id?: number;
  created_by_advertiser?: number;
  default: boolean;
  created_at: string;
  updated_at: string;
  publish_status: string;
}

export interface Advertiser {
  id: number;
  name: string;
  active: boolean;
  legacy_id?: number;
  subscription_start_date: string;
  subscription_end_date: string;
  property_management_company?: number;
  created_at: string;
  updated_at: string;
}

export interface Tag {
  id: string;
  name: string;
  categoryName?: string;
}

export interface Icon {
  id: string;
  image: Image;
  name: string;
}

export enum ActionType {
  Phone = 'phone',
  Email = 'email',
  Web = 'web',
  Directions = 'directions',
}

export interface Action {
  id: string;
  title: string;
  url: string;
  icon: Icon;
  action_type: ActionType;
}

export interface BusinessLocation {
  id: number;
  name: string;
  default: boolean;
  advertiser: number;
  street: string;
  city: string;
  state: string;
  postal_code: string;
  latitude: number;
  longitude: number;
  country: number;
  unit: string;
  contact_email?: string;
  contact_phone?: string;
  contact_website?: string;
  business_hours: BusinessHour[];
}

export interface BusinessHour {
  id: string;
  day_name: string;
  open_hours: string;
}

export interface FeatureType {
  name: string;
  text: string;
}

export interface Advertisement {
  actions: Action[];
  active: boolean;
  advertiser: Advertiser;
  bi_line: string;
  body: string | null;
  business_hours: BusinessHour[];
  campaign: Campaign | null;
  carousel_images: Image[] | null;
  created_at: string;
  featured: boolean;
  headline: string;
  hero_image: Image;
  id: string;
  legacy_id?: number;
  promoted_feature: boolean | null;
  show_map: boolean;
  start_datetime: string | null;
  stop_datetime: string | null;
  tags: Tag[];
  title: string | null;
  updated_at: string;
  hide_overlay_text: boolean;
  feature_type: FeatureType;
  closest_location: BusinessLocation;
  categories: Category[];
  kind: Provider.Zonetail; // this does not work, other than to say it can only be Zonetail
}

export interface FullCategory {
  adverts: Advertisement[];
  featured: boolean;
  id: string;
  title: string;
  age_gated_min_age: null | number;
}

export type CategoryList = Record<string, FullCategory>;

export interface Attachments {
  name: string;
  id: number;
  url: string;
}

export interface NewsItem {
  attachments: Attachments[];
  body: string;
  gallery: Image[];
  id: number;
  title: string;
  updated_at: string;
}

export interface Gallery {
  description: string;
  images: Image[];
  id: number;
  title: string;
}

export interface RequestServicePayload {
  category: string;
  sub_category?: string;
  brief_description: string;
  description: string;
  allow_access: boolean;
}

export interface AssignActivationParams {
  t_code: string;
  email: string;
  unit_id: string;
  property_id: string;
}

export interface ActivateAccountParams {
  email: string;
  activation_code: string;
  unit_id: string;
  property_id: string;
  t_code?: string | null;
}

export interface ConfirmPasswordParams {
  password: string;
  t_code: string;
  property_id: string;
  activation_code: string;
}

export interface ServiceRequestConfig {
  call_center_heading: string;
  call_center_phone_number: string;
  call_center_button_title: string;
}

export interface ServiceRequestCategory {
  name: string;
  id: string;
  subCategories?: string[];
}

export enum Country {
  US = 'US',
  Canada = 'Canada',
}

export enum Locale {
  en = 'en',
  es = 'es',
}

export enum PropertyType {
  condo = 'condo',
  apartment = 'apartment',
}

export interface ExploreProperty {
  id: number;
  active: boolean;
  city: string;
  country: Country;
  created_at: string;
  enable_custom_category_order: boolean;
  latitude: number;
  legacy_address_id?: number;
  locale: Locale;
  longitude: number;
  name: string;
  postal_code: string;
  property_management_corporation: number;
  property_type: PropertyType;
  refId: number;
  show_thumbtack: boolean;
  state: string;
  summary: string;
  updated_at: string;
  street?: string;
  deleted?: boolean;
  archived?: boolean;
  website?: string;
  version?: string;
  unit?: string;
  property_management_company?: number;
  custom_category_order?: any[];
  buisness_address?: string;
}

export enum Provider {
  Zonetail = 'Zonetail',
  Thumbtack = 'Thumbtack',
  ThumbtackCategory = 'ThumbtackCategory',
}

export enum ThumbtackPropertyTypes {
  unknown = 'unknown',
  apartment = 'apartment',
  single_family_home = 'single_family_home',
  commercial = 'commercial',
  townhouse = 'townhouse',
  multi_family = 'multi_family',
  other = 'other',
}

export interface ThumbtackCategoriesObject {
  id: number;
  taskDetails: {
    title?: string;
    description?: string;
    tagline?: string;
    avgCost?: {
      max: number;
      min: number;
    };
  };
  categoryPK?: string;
  taskPK?: string;
  categoryName: string;
  categoryDisplayName: string;
  activeServices: number | null;
  url: string;
  imageURL: string;
  kind?: Provider.ThumbtackCategory;
}

export interface ThumbtackHomecare {
  id: number;
  taskDetails: {
    title?: string;
    description?: string;
    tagline?: string;
    avgCost?: {
      max: number;
      min: number;
    };
  };
  categoryPK?: string;
  taskPK?: string;
  categoryName: string;
  categoryDisplayName: string;
  activeServices: number;
  url: string;
  imageURL: string;
  kind?: Provider.ThumbtackCategory;
}

// export interface ThumbtackCategoriesResponse {
//   results: ThumbtackCategoriesObject[];
// }

export interface ThumbtackProsResponse {
  results: ThumbtackPro[];
}

export interface ThumbtackHomecareResponse {
  results: ThumbtackHomecare[];
}

export interface ThumbtackPro {
  service_id: string;
  id: string;
  business_name: string;
  rating: number;
  num_reviews: number;
  years_in_business: number;
  num_hires: number;
  thumbtack_url: string;
  image_url: string;
  background_image_url: string;
  featured_review: string;
  quote: {
    starting_cost: number;
    cost_unit: number;
  };
  kind: Provider.Thumbtack;
}

export function isThumbtackCategory(
  item:
    | ThumbtackPro
    | Advertisement
    // | ThumbtackCategoriesObject
    | ThumbtackHomecare
    | null
    | undefined,
): item is ThumbtackHomecare {
  // | ThumbtackCategoriesObject
  return item?.kind === Provider.ThumbtackCategory;
}

export function isThumbtackPro(
  item:
    | ThumbtackPro
    | Advertisement
    // | ThumbtackCategoriesObject
    | ThumbtackHomecare
    | null
    | undefined,
): item is ThumbtackPro {
  return item?.kind === Provider.Thumbtack;
}

export function isAdvertisement(
  item:
    | ThumbtackPro
    | Advertisement
    // | ThumbtackCategoriesObject
    | ThumbtackHomecare
    | null
    | undefined,
): item is Advertisement {
  if (item) {
    return (
      item.kind === Provider.Zonetail ||
      !!(item as unknown as Advertisement).business_hours
    );
  }
  return false;
}

export interface FullThumbtackCategory {
  id: number;
  title?: string;
  //adverts is really categories
  adverts?: ThumbtackHomecare[];
  // adverts?: ThumbtackCategoriesObject[];
}
export interface SendFeedbackParams {
  [key: string]: any;
}

export interface RegisterPushNotificationParams {
  [key: string]: any;
}

export interface SubscribePushNotificationParams {
  [key: string]: any;
}

export interface UnSubscribePushNotificationParams {
  [key: string]: any;
}

export interface ImageConfig {
  url: string;
  alternativeText?: string;
  height?: number;
  width?: number;
}

let adserverBase: string;
let zonetailHomeServerBase: string;
let yardiAuthServer;
let yardiUserServer;

enum ServiceStage {
  local = 'local',
  dev = 'dev',
  staging = 'staging',
  prod = 'prod',
}

let serviceStage: ServiceStage;

enum ZonetailHomeAPIServers {
  local = 'http://192.168.2.38:1337',
  dev = 'https://pms-development.zonetail-api.com',
  staging = 'https://api-staging.zonetail-api.com',
  prod = 'https://pms.zonetail-api.com',
}

enum ExploreAPIServers {
  local = 'http://192.168.2.38:1338',
  dev = 'https://api-development.zonetail-api.com',
  staging = 'https://api-staging.zonetail-api.com',
  prod = 'https://explore-home.zonetail-api.com',
}

enum YardiAPIServers {
  local = 'https://yardi-api.zonetail-api.com',
  dev = 'https://yardi-api.zonetail-api.com',
  staging = 'https://yardi-api.zonetail-api.com',
  prod = 'https://yardi-api-prod.zonetail-api.com',
}

/*
'123','zonetal1','adam.hunter@zonetail.com','59646df8-7f62-446c-b716-90a3e71d1b02','t0050117','3',NULL,NULL,'201','8'

*/

/**
 * Cognito Pool Settings
 */

const DevAuth = {
  // REQUIRED - Amazon Cognito Region
  region: 'us-east-1',
  // OPTIONAL - Amazon Cognito User Pool ID
  userPoolId: 'us-east-1_vygd5sGmE',
  // OPTIONAL - Amazon Cognito Web Client ID (26-char alphanumeric string)
  userPoolWebClientId: '6c6a4ltrfmvsi4tdlkumkn6d6p',
  // OPTIONAL - Enforce user authentication prior to accessing AWS resources or not
  mandatorySignIn: false,
  // OPTIONAL - Manually set the authentication flow type. Default is 'USER_SRP_AUTH'
  authenticationFlowType: 'USER_PASSWORD_AUTH',
  // OPTIONAL - Manually set key value pairs that can be passed to Cognito Lambda Triggers
  clientMetadata: { appId: 'com.zonetail.pms' },
  // OPTIONAL - Hosted UI configuration
  oauth: {},
};

// This is the production cognito server
const ProdAuth = {
  // REQUIRED - Amazon Cognito Region
  region: 'us-east-1',
  // OPTIONAL - Amazon Cognito User Pool ID
  userPoolId: 'us-east-1_c4uPypsVB',
  // OPTIONAL - Amazon Cognito Web Client ID (26-char alphanumeric string)
  userPoolWebClientId: '2t2tm56ad1f7bcrv9kelqnbhvo',
  // OPTIONAL - Enforce user authentication prior to accessing AWS resources or not
  mandatorySignIn: false,
  // OPTIONAL - Manually set the authentication flow type. Default is 'USER_SRP_AUTH'
  authenticationFlowType: 'USER_PASSWORD_AUTH',
  // OPTIONAL - Manually set key value pairs that can be passed to Cognito Lambda Triggers
  clientMetadata: { appId: 'com.zonetail.pms' },
  // OPTIONAL - Hosted UI configuration
  oauth: {},
};

const resolveAuthStage = (stage: ServiceStage) => {
  switch (stage) {
    case ServiceStage.dev:
      return DevAuth;
    case ServiceStage.local:
      return DevAuth;
    case ServiceStage.prod:
      return ProdAuth;
  }
};

// if (__DEV__ || true) {
//   debug('using dev config');
//   serviceStage = ServiceStage.local;
// } else {
//  debug('using production config');
serviceStage = ServiceStage.staging;
//}

switch (serviceStage as ServiceStage) {
  case ServiceStage.prod:
    yardiAuthServer = YardiAPIServers.prod;
    yardiUserServer = YardiAPIServers.prod;
    adserverBase = ExploreAPIServers.prod;
    zonetailHomeServerBase = ZonetailHomeAPIServers.prod;
    break;
  case ServiceStage.staging:
    yardiAuthServer = YardiAPIServers.staging;
    yardiUserServer = YardiAPIServers.staging;
    adserverBase = ExploreAPIServers.staging;
    zonetailHomeServerBase = ZonetailHomeAPIServers.staging;
    break;
  case ServiceStage.local:
    yardiAuthServer = YardiAPIServers.local;
    yardiUserServer = YardiAPIServers.local;
    adserverBase = ExploreAPIServers.local;
    zonetailHomeServerBase = ZonetailHomeAPIServers.local;
    break;
  case ServiceStage.dev:
  default:
    yardiAuthServer = YardiAPIServers.dev;
    yardiUserServer = YardiAPIServers.dev;
    adserverBase = ExploreAPIServers.dev;
    zonetailHomeServerBase = ZonetailHomeAPIServers.dev;
    break;
}
const yardiAuthAPI = `${yardiAuthServer}/auth`;
const yardiUserAPI = `${yardiUserServer}/user`;

export const Auth = resolveAuthStage(serviceStage);

export const ServerConfigs = {
  yardiAuthServer,
  yardiUserServer,
  adserverBase,
  zonetailHomeServerBase,
  Auth,
};

export const headerConfig = {};

async function getCondoCorpId() {
  const id = await AsyncStorage.getItem(CONDO_CORP_ID);
  return id;
}

async function getPropertyManagerId() {
  const id = await AsyncStorage.getItem(PROPERTY_MANAGER_ID);
  return id;
}

function getStandardParams() {
  return {
    deviceType: Platform.OS,
    bundleId: DeviceInfo.getBundleId(),
    appVersion: DeviceInfo.getVersion(),
  };
}

export async function getCategories(building: string) {
  const url = `${adserverBase}/feed/${building}/categories`;

  const { data } = await axiosInstancePlain.get<Category[]>(url);
  return data;
}

export async function getGatedCategories(building: string) {
  const url = `${adserverBase}/feed_gated/${building}/categories`;

  const { data } = await axiosInstancePlain.get<Category[]>(url);
  return data;
}

export async function getAdverts(building: string) {
  try {
    const url = `${adserverBase}/feed/${building}`;
    console.log(`fetching adverts from ${url}`);
    const startFetch = Date.now();
    let response = await fetch(url, {});
    let responseJson = await response.json();
    console.log(
      `advert fetch (fetch) duration: ${Date.now() - startFetch} (ms)`,
    );
    return responseJson as unknown as CategoryList;
  } catch (e) {
    console.log(e);
    return {};
  }
}

export async function getGatedAdverts(building: string) {
  try {
    const url = `${adserverBase}/feed_gated/${building}`;
    // console.log(`getGatedAdverts(${url})`);
    const startFetch = Date.now();
    let response = await fetch(url, {});
    let responseJson = await response.json();
    console.log(
      `advert gated fetch (fetch) duration: ${Date.now() - startFetch} (ms)`,
    );
    return responseJson as unknown as CategoryList;
  } catch (e) {
    console.log(e);
    return {};
  }
}

export async function getAgeGatedTerms() {
  return axios.get(`${adserverBase}/age-gate-terms`);
}

export async function submitBuildingSelectionAPI(code: string) {
  try {
    const url = `${zonetailHomeServerBase}/condo-corporations?building_code=${code}`;
    const { data } = await axiosInstancePlain.get(url, headerConfig);
    // console.log('data', data);

    return Array.isArray(data) && data.length > 0;
  } catch (e) {
    const fullError = e as unknown as any;
    console.log(
      'submitBuildingSelectionAPI: api response err',
      fullError?.response?.data,
    );
    return false;
  }
}

export async function fetchExploreProperty(applicationName: string) {
  try {
    const url = `${adserverBase}/properties?property_management_corporation.application_name=${applicationName}&_sort=id:DESC&_limit=1`;
    console.log(`fetch exlore property url:${url}`);
    const response = await fetch(url, {});
    const responseJson: ExploreProperty[] = await response.json();
    if (isArray(responseJson) && !isEmpty(responseJson)) {
      return responseJson[0];
    }
  } catch (e) {
    console.error(e);
    return '';
  }
}

export async function fetchCondoDataAPI(code: string) {
  //  try {
  const url = `${zonetailHomeServerBase}/condo-corporations/${code}`;
  const { data }: { data: CondoCorporation } = await axiosInstancePlain.get(
    url,
    headerConfig,
  );
  console.log('fetchCondoDataAPI', data);
  return data;
  // } catch (e) {
  //   console.log('submitBuildingSelectionAPI: api response err', e?.response?.data);
  //   return false;
  // }
}

export async function submitNewsListAPI() {
  const propertyId = await getCondoCorpId();
  const url = `${zonetailHomeServerBase}/community-news-for-condo/${propertyId}?_sort=updated_at:DESC`;
  const params = getStandardParams();
  const { data } = await axiosInstancePlain.get(url, { params });
  return data;
}

export async function getGalleryListAPI() {
  const propertyId = await getCondoCorpId();
  const url = `${zonetailHomeServerBase}/community-galleries-for-condo/${propertyId}?_sort=updated_at:DESC`;
  const params = getStandardParams();
  const { data } = await axiosInstancePlain.get(url, { params });
  return data;
}

export async function getCommunityCalendarAPI() {
  const propertyId = await getCondoCorpId();
  const url = `${zonetailHomeServerBase}/community-calendars-for-condo/${propertyId}`;
  const params = getStandardParams();
  const { data } = await axiosInstancePlain.get(url, { params });
  // console.log('calendar data', data);
  return data;
}

export async function getPropertyManagerAPI() {
  const propertyId = await getCondoCorpId();
  const propertyManagerId = await getPropertyManagerId();
  const url = `${zonetailHomeServerBase}/property-managers/${propertyManagerId}`;
  // const params = getStandardParams();
  // console.log('getPropertyManagerAPI', {url, params});
  const { data }: { data: PropertyManager } = await axiosInstancePlain.get(
    url,
    {
      params: {
        current_condo_corp_id: propertyId,
      },
    },
  );
  return data;
}

export async function getServiceRequestConfig() {
  const propertyId = await getCondoCorpId();
  const url = `${zonetailHomeServerBase}/feed/service_request_config/${propertyId}`;
  const { data } = await axiosInstancePlain.get(url, {});
  return data as {} as ServiceRequestConfig;
}

export async function getUserProfileAPI() {
  try {
    const url = `${yardiUserAPI}/user`;
    // console.log('headers fox auth are:', axiosInstance.defaults.headers.common.Authorization);
    const params = getStandardParams();
    const { data } = await axiosInstance.get(url, { params });
    // console.log('response for getprofile api is:', data);
    return data;
  } catch (error: any | AxiosError) {
    if (axios.isAxiosError(error)) {
      console.log('error with getUSerProfileAPI', error.response);
    } else {
      console.log('error with getUSerProfileAPI', JSON.stringify(error));
    }
    throw error;
  }
}

export async function deleteUserProfileAPI() {
  try {
    const url = `${yardiUserAPI}/user/delete`;
    // console.log('headers fox auth are:', axiosInstance.defaults.headers.common.Authorization);
    const params = getStandardParams();
    const { data } = await axiosInstance.delete(url, { params });
    // console.log('response for getprofile api is:', data);
    return data;
  } catch (error: any | AxiosError) {
    if (axios.isAxiosError(error)) {
      console.log('error with deleteUserProfileAPI', error.response);
    } else {
      console.log('error with deleteUserProfileAPI', JSON.stringify(error));
    }
    throw error;
  }
}

export async function getServiceRequestAPI() {
  const url = `${yardiUserAPI}/users/service_requests`;
  const params = getStandardParams();
  const { data } = await axiosInstance.get(url, { params });
  //  console.log('get service requests', data);
  return data;
}

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function createServiceRequestAPI(params: RequestServicePayload) {
  const url = `${yardiUserAPI}/users/service_requests`;
  const standardParams = getStandardParams();
  const res = await axiosInstance.post(url, { ...params, standardParams });
  return res;
}

export async function getServiceCategories() {
  const url = `${yardiUserAPI}/service_requests/categories`;
  const params = getStandardParams();
  const { data } = await axiosInstance.get(url, { params });
  return { categories: data.data as ServiceRequestCategory[] };
}

export async function getOTPAPI(email: string, unit_id: string) {
  // THIS IS THE ONE
  const url = `${yardiAuthAPI}/auth/activate`;
  let params: object;
  params = {
    ...getStandardParams(),
    email,
    unit_id,
  };
  // console.log('getOTPAPI', {url, params});
  try {
    const { data } = await axios.post(url, params);
    // dummy data
    // const data = {
    //   unit_id,
    //   property_id: 234,
    //   tcode: 123,
    //   email: emailOrTcode,
    //   success: true,
    // };
    return data;
  } catch (e) {
    console.warn('getOTPAPI error', e);
    throw e;
  }
}

export async function getTcodeLookup(t_code: string, unit_id: string) {
  // THIS IS THE ONE
  const url = `${yardiAuthAPI}/auth/tcodeLookup`;
  const params = {
    ...getStandardParams(),
    t_code,
    unit_id,
  };
  // console.log('getOTPAPI', {url, params});
  try {
    const { data } = await axios.post(url, params);
    // dummy data
    // const data = {
    //   unit_id,
    //   property_id: 234,
    //   tcode: 123,
    //   email: emailOrTcode,
    //   success: true,
    // };
    return data;
  } catch (error: any | AxiosError) {
    if (axios.isAxiosError(error)) {
      console.log('error with getTcodeLookup', error.response);
    } else {
      console.log('error with getTcodeLookup', JSON.stringify(error));
    }
    throw error;
  }
}

export async function assignActivationEmail(
  assignActivationParams: AssignActivationParams,
) {
  const url = `${yardiAuthAPI}/auth/activateUserTcode`;
  try {
    // Placeholder code
    const params = {
      ...getStandardParams(),
      t_code: assignActivationParams.t_code,
      email: assignActivationParams.email,
      unit_id: assignActivationParams.unit_id,
    };
    const { data } = await axios.post(url, params);
    return data;
  } catch (e: any) {
    console.warn('assignActivateEmail error', e);
    throw e;
  }
}

export async function activeAccountAPI(
  activateAccountParams: ActivateAccountParams,
) {
  const url = `${yardiAuthAPI}/auth/register`;
  try {
    const params = { ...getStandardParams(), ...activateAccountParams };
    // console.log({url, params});
    const { data } = await axiosInstance.post(url, params);
    // console.log('activate account api response is:', data);
    return data;
  } catch (e) {
    console.warn('activeAccountAPI', e);
    throw e;
  }
}

export async function confirmPasswordAPI(
  passwordParams: ConfirmPasswordParams,
) {
  const url = `${yardiAuthAPI}/user/create`;
  try {
    const params = { ...getStandardParams(), ...passwordParams };
    const { data } = await axiosInstance.post(url, params);
    // console.log('confirmPasswordAPI', JSON.stringify(data, null, 2));
    return data;
  } catch (e) {
    console.warn('confirmPasswordAPI', e);
    throw e;
  }
}

export async function sendFeedbackAPI(feedbackParams: SendFeedbackParams) {
  const url = `${yardiUserAPI}/users/feedback`;
  const params = { ...getStandardParams(), ...feedbackParams };
  const { data } = await axiosInstance.post(url, params);
  return data;
}

export async function registerPushNotificationAPI(
  registerParams: RegisterPushNotificationParams,
) {
  const url = `${yardiUserAPI}/push_notification`;
  const params = { ...getStandardParams(), ...registerParams };
  console.log('registering push notification with params', params);
  const { data } = await axiosInstance.post(url, params);
  console.log('registered with data response:', data);
  return data;
}

export async function subscribePushNotificationAPI(
  subscribeParams: SubscribePushNotificationParams,
) {
  const url = `${yardiUserAPI}/subscribe`;
  try {
    const params = { ...getStandardParams(), ...subscribeParams };
    console.log('subscribing with params', params);
    const { data } = await axiosInstance.post(url, params);
    console.log('subscribed with data response', data);
    return data;
  } catch (e) {
    console.error(e);
    throw e;
  }
}

export async function unSubscribePushNotificationAPI(
  unsubscribeParams: UnSubscribePushNotificationParams,
) {
  const url = `${yardiUserAPI}/unsubscribe`;
  const params = { ...getStandardParams(), ...unsubscribeParams };
  const { data } = await axiosInstance.post(url, params);
  return data;
}

export async function checkForForceUpdate() {
  const url = `${zonetailHomeServerBase}/force-update/shouldUpdate`;
  const params = getStandardParams();
  const { data } = await axiosInstancePlain.get(url, { params });
  return data;
}

export async function getPropertyConfig(
  yardiPropertyId: string,
  yardiPropertyName: string,
  yardiId: string,
  yardiReferenceId: string,
) {
  try {
    const url = `${zonetailHomeServerBase}/property-managers/property_config`;
    const params = {
      deviceType: Platform.OS,
      bundleId: DeviceInfo.getBundleId(),
      appVersion: DeviceInfo.getVersion(),
      yardiPropertyId,
      yardiPropertyName,
      yardiId,
      yardiReferenceId,
    };
    // console.log(`getPropertyConfig: ${url} ${JSON.stringify(params)}`);
    const { data } = await axiosInstancePlain.get(url, { params });
    return data;
  } catch (e) {
    console.warn('getPropertyConfig error', e);
    throw e;
  }
}

export async function getHomePropertyConfig(propertyId: string) {
  try {
    const url = `${zonetailHomeServerBase}/property-managers/home_property_config`;
    const params = {
      deviceType: Platform.OS,
      bundleId: DeviceInfo.getBundleId(),
      appVersion: DeviceInfo.getVersion(),
      propertyId,
    };
    // console.log(`getPropertyConfig: ${url} ${JSON.stringify(params)}`);
    const { data } = await axiosInstancePlain.get(url, { params });
    return data;
  } catch (e) {
    console.warn('getPropertyConfig error', e);
    throw e;
  }
}

export async function getContestFieldApi(building: string) {
  const url = `${zonetailHomeServerBase}/building-contests/${building}`;
  const params = getStandardParams();
  // console.log(`getContestFieldApi: ${url} ${building}`);
  const { data } = await axios.get(url, { params });
  return data;
}

export async function submitContestApi(params: any) {
  const url = `${yardiUserAPI}/contest`;
  const { data } = await axiosInstance.post(url, params);
  return data;
}

// pulling from prod since their test api doesn't work
const thumbtackCategoriesBase =
  'https://api.thumbtack.com/v1/partners/discoverylite/categories';
const thumbtackProsBase =
  'https://api.thumbtack.com/v1/partners/discoverylite/pros';
const thumbtackHomeCareBase =
  'https://pro-api.thumbtack.com/v1/partners/discoverylite/homecare/checklist';

export const thumbtackHeaderConfig = {
  headers: {
    Authorization:
      'Basic Wm9uZXRhaWw6b2FmdzdwNjhaMVNYZ3ZLQkJXSDBOU1ZLdTNzaTF1UXR6cFRXb1VoS3JNdz0=',
  },
};

const utmSource = 'cma-zonetail';

export async function getZipcode(id: string) {
  const condoData = await fetchCondoDataAPI(id);
  console.log('zip_postal', JSON.stringify(condoData));
  if (condoData && condoData.zip_postal) {
    const zipPostal = condoData.zip_postal;
    return zipPostal;
  }
  console.error('getZipcode: no zipcode data');
  return '';
}

// export async function getThumbtackCategories() {
//   const zipCode = await AsyncStorage.getItem(ZIP_POSTAL);
//   if (!zipCode) {
//     console.error('unable to resolve zipcode');
//   } else {
//     console.log('zipcode is:', zipCode);
//   }
//   const url = `${thumbtackCategoriesBase}?zip_code=${zipCode}&utm_source=${utmSource}`;
//   try {
//     const { data } = await axios.get(url, thumbtackHeaderConfig);
//     data.results.sort(
//       (catA: ThumbtackCategoriesObject, catB: ThumbtackCategoriesObject) => {
//         if (!catA.activeServices && !catB.activeServices) {
//           return 0;
//         }
//         if (!catA.activeServices && catB.activeServices) {
//           return 1;
//         }
//         if (catA.activeServices && !catB.activeServices) {
//           return -1;
//         }
//         if (catA.activeServices && catB.activeServices) {
//           if (catA.activeServices > catB.activeServices) {
//             return -1;
//           }
//           if (catA.activeServices < catB.activeServices) {
//             return 1;
//           }
//         }
//         return 0;
//       },
//     );
//     if (data && data.results) {
//       data.results.map((result: ThumbtackCategoriesObject, idx: number) => {
//         result.kind = Provider.ThumbtackCategory;
//         result.id = idx;
//       });
//     }
//     return data as ThumbtackCategoriesResponse;
//   } catch (e: any) {
//     console.error('trouble with thumbtack category api: ', e);
//   }
// }

export async function getThumbtackPros(category: string) {
  const zipCode = await AsyncStorage.getItem(ZIP_POSTAL);
  if (!zipCode) {
    console.error('unable to resolve zipcode');
  } else {
    console.log('zipcode is:', zipCode);
  }
  const url = `${thumbtackProsBase}?zip_code=${zipCode}&category=${category}&utm_source=${utmSource}`;
  console.log(`getThumbtackPros: ${url}`);
  try {
    const { data, status } = await axios.get(url, thumbtackHeaderConfig);
    if (status !== 200) {
      return null;
    }
    const { results }: ThumbtackProsResponse = data;

    results.sort((catA, catB) => {
      const ratingA = catA.rating;
      const ratingB = catB.rating;

      if (!ratingA && !ratingB) {
        return 0;
      }
      if (!ratingA && ratingB) {
        return 1;
      }
      if (ratingA && !ratingB) {
        return -1;
      }
      if (ratingA && ratingB) {
        if (ratingA > ratingB) {
          return -1;
        }
        if (ratingA < ratingB) {
          return 1;
        }
      }
      return 0;
    });

    results.map((thumbtackPro) => {
      thumbtackPro.kind = Provider.Thumbtack;
      thumbtackPro.id = thumbtackPro.service_id;
    });

    return results as ThumbtackPro[];
  } catch (e: any) {
    console.error('trouble with thumbtack pros api: ', e);
  }
}

export async function getThumbtackHomecare(
  property_type: ThumbtackPropertyTypes,
) {
  try {
    const zipCode = await AsyncStorage.getItem(ZIP_POSTAL);
    const url = `${thumbtackHomeCareBase}?zip_code=${zipCode}&property_type=${property_type}&utm_source=${utmSource}`;
    // const url = 'thumbtack_homecare_response.json';
    const { data }: { data: ThumbtackHomecareResponse } = await axios.get(
      url,
      thumbtackHeaderConfig,
    );
    data.results.sort((catA: ThumbtackHomecare, catB: ThumbtackHomecare) => {
      if (!catA.activeServices && !catB.activeServices) {
        return 0;
      }
      if (!catA.activeServices && catB.activeServices) {
        return 1;
      }
      if (catA.activeServices && !catB.activeServices) {
        return -1;
      }
      if (catA.activeServices && catB.activeServices) {
        if (catA.activeServices > catB.activeServices) {
          return -1;
        }
        if (catA.activeServices < catB.activeServices) {
          return 1;
        }
      }
      return 0;
    });
    // Adding an id to make it consistant with other apis
    data.results.forEach((result, idx) => {
      result.id = idx;
      result.kind = Provider.ThumbtackCategory;
    });
    return data;
  } catch (e: any) {
    console.error(e);
  }
}

export async function getImageConfig(building: string) {
  const url = `${adserverBase}/feed/config/${building}`;

  try {
    const { data }: { data: ImageConfig } = await axios.get(url, {});
    return data;
  } catch (e: any) {
    console.log(e);
  }
}
